import { useDisclosure } from "@heroui/react";
import { ModalBuilder, ModalError, ModalField } from "./../modalBuilder";
import { api } from "@/api";
import { toast } from "react-toastify";
import {
  getIdFromRenterHandbook,
  getIdFromUserHandbook,
} from "@/utils/getIdFromHandbooks.ts";
import useErrorHandling from "@/hooks/useErrorHandling";
import { useState } from "react";
import { MeetingRoom, MeetingRoomRent } from "@/api/meetingRooms";
import ym from "react-yandex-metrika";
import { CodependentRenterEmployeeHandbook } from "../../handbooks/userHandbook";
import { normalizeDatetime } from "@/utils";

interface EditMeetingRoomRentForm {
  renter_id: number;
  user_id: number;
  rent_from: string;
  rent_to: string;
  description: string;
}

const editFields: ModalField[] = [
  {
    label: "Компания",
    name: "renter_id",
    placeholder: "",
    type: "renter",
    isRequired: true,
  },
  {
    label: "Пользователь",
    name: "user_id",
    placeholder: "",
    type: "codependent",
    deps: ["renter_id"],
    element: CodependentRenterEmployeeHandbook,
    isRequired: true,
  },
  {
    label: "Время начала",
    name: "rent_from",
    placeholder: "1",
    type: "datetime-local",
    isRequired: true,
  },
  {
    label: "Время конца",
    name: "rent_to",
    placeholder: "1",
    type: "datetime-local",
    isRequired: true,
  },
  {
    label: "Описание",
    name: "description",
    placeholder: "",
    type: "text",
    isRequired: false,
  },
];

export function EditMeetingRoomRentModal({
  rent,
  disclosure,
  meetingRoom,
}: {
  rent: MeetingRoomRent | null;
  disclosure: ReturnType<typeof useDisclosure>;
  meetingRoom: MeetingRoom | null;
}) {
  const { isOpen, onOpenChange } = disclosure;
  const [modalErrors, setModalErros] = useState<ModalError[]>([]);

  const handleError = useErrorHandling();

  const onSubmit = (data: EditMeetingRoomRentForm) => {
    return new Promise<boolean>(async (resolve, _reject) => {
      const renter = await getIdFromRenterHandbook(String(data.renter_id));
      const user = await getIdFromUserHandbook(String(data.user_id));

      api.meetingRooms
        .updateRent(rent?.id || 0, {
          meeting_room_id: meetingRoom?.id || 0,
          rent_from: data.rent_from,
          rent_to: data.rent_to,
          renter_id: Number(renter),
          user_id: Number(user),
          description: data.description,
        })
        .then((_data) => {
          ym("reachGoal", "meeting_room_rented");
          disclosure.onClose();
          resolve(true);
        })
        .catch((err) => {
          console.log(err);
          const { errorMessage, parsedErrors } = handleError(err);
          setModalErros(parsedErrors || []);
          toast.error(errorMessage);
          resolve(false);
        });
    });
  };

  const onDelete = () => {
    return new Promise<boolean>(async (resolve, _reject) => {
      api.meetingRooms
        .cancelRent(rent?.id || 0)
        .then((_data) => {
          ym("reachGoal", "meeting_room_canceled");
          disclosure.onClose();
          resolve(true);
        })
        .catch((err) => {
          console.log(err);
          const { errorMessage, parsedErrors } = handleError(err);
          setModalErros(parsedErrors || []);
          toast.error(errorMessage);
          resolve(false);
        });
    });
  };

  return (
    <ModalBuilder
      title="Изменение брони"
      modalErrors={modalErrors}
      fields={editFields}
      isOpen={isOpen}
      isSensitiveData={false}
      onOpenChange={onOpenChange}
      onSubmit={onSubmit}
      onDelete={onDelete}
      submitButtonText="Сохранить"
      defaultValues={{
        renter_id: rent?.renter_id || "",
        user_id: rent?.user_id || "",
        rent_from: normalizeDatetime(rent?.rent_from),
        rent_to: normalizeDatetime(rent?.rent_to),
        description: rent?.description || "",
      }}
    />
  );
}
